import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom, map } from 'rxjs';
import { Damage, Smartphoto, SmartphotoImage } from '@bdeo-motor/smartphoto-entities';
import { ISmartphoto } from '@bdeo-platform/private-api';
import { BdeoToastService } from '@bdeo/webcomponents';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { CompanyService } from './company.service';
import { TransactionService } from './transaction.service';
import { DamageImagesService } from './damage-images.service';
import { environment } from '../../environments/environment';
import { orderByType } from '../entities/types/order-by.type';
import { SmartphotoStatusDetails } from '../entities/types/smartphoto-status-details.type';
import { IFilterQuery } from '../entities/interfaces/services/filter-query.interface';
import { StatusColor, StatusIcon, StatusLang } from '../entities/enums/status.enum';
import { Transaction } from '../entities/classes/transaction.class';
import { SmartphotoAbilities } from '../entities/classes/smartphoto-abilities.class';
import { CreateResponse, DamagesUpdatePayload, GetListResponse, SmartphotoRepositoryService } from './smartphoto-repository.service';
import { AuthService } from './auth.service';
import { DamageEstimationRequest, DamageEstimationResponse } from '../entities/types/damage-estimation.type';
import { cleanExportedDamages } from '../utils/damage-user-fields-cleaner.util';

export type DamageAction = {
  label: string;
  icon: string;
  color: string;
  callback: (damage: Damage) => void;
};

type DamageImageFile = { file: File; uuid: string };

@Injectable({
  providedIn: 'root'
})
export class SmartphotoService {
  private readonly authService = inject(AuthService);
  private readonly smartphotoRepositoryService = inject(SmartphotoRepositoryService);
  private readonly i18NextService = inject(I18NEXT_SERVICE);
  private readonly transactionService = inject(TransactionService);
  private readonly companyService = inject(CompanyService);
  private readonly smartphoto$ = new BehaviorSubject<Smartphoto | undefined>(undefined);
  private readonly actionsDamage$ = new BehaviorSubject<Damage | undefined>(undefined);
  private readonly currentSmartphotoImages$ = new BehaviorSubject<{ files: DamageImageFile[]; damage: string }[]>([]);
  private readonly damageImagesService = inject(DamageImagesService);
  public readonly abilities = new SmartphotoAbilities();
  public readonly images$ = this.smartphoto$.pipe(
    map((smartphoto) => {
      return smartphoto?.images.all.filter((image) => !['car', 'signature'].includes(image.type)) ?? [];
    })
  );
  public statusDetails$ = this.smartphoto$.pipe(
    map((smartphoto) => {
      return this.getSmartphotoStatusDetails(smartphoto);
    })
  );
  public canLoadVlc$ = new Subject<void>();
  private originalSmartphoto$ = new BehaviorSubject<ISmartphoto | undefined>(undefined);

  public getOriginalSmartphoto(): ISmartphoto | undefined {
    return this.originalSmartphoto$.getValue();
  }

  public get actionsDamageObservable(): Observable<Damage | undefined> {
    return this.actionsDamage$.asObservable();
  }

  public async getSmartphoto(smartphotoId: string): Promise<Smartphoto> {
    try {
      const entity = await this.smartphotoRepositoryService.find(smartphotoId);
      const company = await this.companyService.getCompany();

      if (!company) {
        throw new Error('Company is mandatory');
      }

      const smartphoto = new Smartphoto(entity, company);
      this.originalSmartphoto$.next(entity);
      this.smartphoto$.next(smartphoto);
      this.canLoadVlc$.next();
      return smartphoto;
    } catch (e: unknown) {
      this.authService.logOut();
      throw e;
    }
  }

  public lookupSmartphoto(smartphotoId: string): Promise<ISmartphoto> {
    return this.smartphotoRepositoryService.find(smartphotoId);
  }

  public async getList(
    orderBy: orderByType,
    asc: boolean,
    filterQuery?: IFilterQuery,
    cursor?: { id: string; value: null | string }
  ): Promise<Smartphoto[]> {
    const { items } = await this.smartphotoRepositoryService.getList(orderBy, asc, filterQuery, cursor);

    return items;
  }

  public async getListWithPagination(
    orderBy: orderByType,
    asc: boolean,
    filterQuery?: IFilterQuery,
    cursor?: { id: string; value: null | string }
  ): Promise<GetListResponse> {
    return await this.smartphotoRepositoryService.getList(orderBy, asc, filterQuery, cursor);
  }

  public get = {
    images: (): Observable<SmartphotoImage[]> => {
      return this.images$;
    }
  };

  public update = {
    basket: async (): Promise<void> => {
      const smartphoto = this.smartphoto$.getValue();
      if (!smartphoto || !smartphoto.basket) {
        return;
      }

      await this.smartphotoRepositoryService.updateBasket(smartphoto.id, smartphoto.basket.name);
    },

    status: async (newStatus: number, fields2Update?: Record<string, unknown>): Promise<void> => {
      const smartphoto = this.smartphoto$.getValue();
      if (!smartphoto) {
        return;
      }

      await this.smartphotoRepositoryService.updateStatus(smartphoto.id, newStatus, fields2Update);
    }
  };

  // TO DO: Mostrar toasts si ha habido un problema creando la Smartphoto o el Case
  // TO DO: Tipar el body de la llamada a create una vez que esté el endpoint en la API nueva.

  public async create(caseID: string, customFields: Record<string, unknown>): Promise<CreateResponse> {
    try {
      return await this.smartphotoRepositoryService.create(caseID, customFields);
    } catch (error) {
      const message = this.i18NextService.t('brokers:errors.case_creation_error');
      BdeoToastService.error(message);
      throw error;
    }
  }

  public async updateDamages(payload: DamagesUpdatePayload): Promise<boolean> {
    return this.smartphotoRepositoryService.updateDamages(payload);
  }

  public getCurrentSmartphoto(): Observable<Smartphoto | undefined> {
    return this.smartphoto$.asObservable();
  }

  public resetSmartphoto(): void {
    const smartphoto = this.originalSmartphoto$.getValue();
    const company = this.companyService.getCurrentCompany();

    if (smartphoto && company) {
      this.smartphoto$.next(new Smartphoto(smartphoto, company));
    }
  }

  public updateSmartphoto(smartphoto: Smartphoto): void {
    this.smartphoto$.next(smartphoto);
  }

  public getSmartphotoStatusDetails(smartphoto: Smartphoto | undefined): SmartphotoStatusDetails | undefined {
    if (!smartphoto) {
      return;
    }

    const status = smartphoto.status;

    return {
      lang: StatusLang[status as keyof typeof StatusLang],
      color: StatusColor[status as keyof typeof StatusLang],
      icon: StatusIcon[status as keyof typeof StatusLang]
    };
  }

  public getSmartphotoReport(): string | undefined {
    return this.smartphoto$.getValue()?.report;
  }

  public getEvidenceCaptureLink(): string {
    const smartphoto = this.smartphoto$.getValue();
    if (!smartphoto) {
      throw new Error('Smartphoto not found.');
    }

    return `${environment.evidenceCaptureURL}/#/?secretKey=id-${smartphoto.id}&broker=true`;
  }

  public getAdditionalsLink(): string | undefined {
    const id = this.smartphoto$.getValue()?.id;

    return id ? `${environment.additionalsURL}/${id}` : undefined;
  }

  public async downloadImages(): Promise<void> {
    const zip = new JSZip();

    const images = await firstValueFrom(this.images$);
    const fileNames: string[] = [];

    const promises: Promise<void>[] = images.map(async (image) => {
      const response = await fetch(image.url, { cache: 'no-store' });
      const blob = await response.blob();
      const fileName = image.getFileName().replace(/\//g, '');
      zip.file(this.checkIfFileNameExists(fileName, fileNames), blob);
    });

    await Promise.all(promises);
    await zip.generateAsync({ type: 'blob' }).then((content) => {
      const enlace = document.createElement('a');
      const objectUrl = URL.createObjectURL(content);
      enlace.href = objectUrl;
      enlace.download = `${this.smartphoto$.getValue()?.numRef}.zip`;
      enlace.click();
      // Revocar después de un breve delay para asegurar que la descarga inició
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
    });
  }

  private checkIfFileNameExists(fileName: string, fileNames: string[]): string {
    let index = 0;

    if (fileNames.includes(fileName)) {
      index++;
      fileName = `${fileName.split('.').slice(0, -1).join('.')}-${index}.${fileName.split('.').pop()}`;
      return this.checkIfFileNameExists(fileName, fileNames);
    } else {
      fileNames.push(fileName);
      return fileName;
    }
  }

  public downloadImage(url: string, name: string): void {
    fetch(url, { cache: 'no-store' })
      .then((response) => response.blob())
      .then((blob) => {
        saveAs(blob, name);
      });
  }

  public createUpdateTransaction(): void {
    if (!this.transactionService.empty) {
      return;
    }

    const smartphoto = this.smartphoto$.getValue();

    if (!smartphoto) {
      throw new Error('Smartphoto is not defined');
    }

    this.transactionService.add(new Transaction('smartphoto-damages-update', () => this.updateTransaction(smartphoto)));
  }

  public async getDamageEstimation(request: DamageEstimationRequest, targetUuid: string): Promise<DamageEstimationResponse> {
    try {
      const estimations = (await this.smartphotoRepositoryService.getDamageEstimation(request))?.estimations ?? [];

      const targetEstimation = estimations.find((estimation) => estimation.uuid === targetUuid) ?? {
        labour_time: 0,
        paint_time: 0,
        paint_materials: 0,
        part_price: 0,
        strip_assembly: 0
      };

      return {
        labour_time: targetEstimation.labour_time,
        paint_time: targetEstimation.paint_time,
        paint_materials: targetEstimation.paint_materials,
        part_price: targetEstimation.part_price ?? 0,
        strip_assembly_time: targetEstimation.strip_assembly
      };
    } catch (error) {
      BdeoToastService.error(this.i18NextService.t('brokers:errors.damage_estimation_error'));
      throw error;
    }
  }

  public selectDamageForActions(damage?: Damage): void {
    this.actionsDamage$.next(damage);
  }

  public getReportName(grouping: string, isMain: boolean, index: number): string {
    if (isMain) {
      return this.i18NextService.t(`brokers:reports.main_report_${grouping}`);
    } else {
      return this.i18NextService.t(`brokers:reports.secondary_report_${grouping}`) + (index > 0 ? ` ${index + 1}` : '');
    }
  }

  public getDamageActions(damage: Damage): DamageAction[] {
    const actions: DamageAction[] = [];
    const currentSmartphoto = this.smartphoto$.getValue();

    if (!currentSmartphoto) {
      return [];
    }

    const damageOtherReport = currentSmartphoto.reports.findOtherReport().find((r) => r.damages.includes(damage));

    // Mostrar mover a daño principal
    if (damageOtherReport) {
      actions.push({
        label: `${this.i18NextService.t('brokers:actions.move_to')} ${this.getReportName(currentSmartphoto.grouping, true, 0)}`,
        icon: 'adjustment-filters',
        color: 'tar-grey',
        callback: () => {
          currentSmartphoto.reports.move(damage, currentSmartphoto.reports.findMainReport().uuid);
          this.actionsDamage$.next(undefined);
          currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
          this.updateSmartphoto(currentSmartphoto);
          this.createUpdateTransaction();
        }
      });
    }

    // Mostrar mover a parte secundario
    currentSmartphoto.reports.findOtherReport().forEach((report, index) => {
      if (report.uuid !== damageOtherReport?.uuid) {
        actions.push({
          label: `${this.i18NextService.t('brokers:actions.move_to')} ${this.getReportName(currentSmartphoto.grouping, false, index)}`,
          icon: 'adjustment-filters',
          color: 'tar-grey',
          callback: () => {
            currentSmartphoto.reports.move(damage, report.uuid);
            this.actionsDamage$.next(undefined);
            currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
            this.updateSmartphoto(currentSmartphoto);
            this.createUpdateTransaction();
          }
        });
      }
    });

    // Mostrar borrar daño
    if (!damage.deleted) {
      actions.push({
        label: 'actions.remove',
        icon: 'action-delete',
        color: 'red-velvet',
        callback: () => {
          currentSmartphoto.reports.deleteDamage(damage);
          this.actionsDamage$.next(undefined);
          currentSmartphoto.estimation?.calculate([...currentSmartphoto.getMainDamages(), ...currentSmartphoto.concepts]);
          this.updateSmartphoto(currentSmartphoto);
          this.createUpdateTransaction();
        }
      });
    }

    return actions;
  }

  public addImagesToCurrentSmartphoto(images: File[], damage: Damage): void {
    if (images.length === 0) {
      return;
    }

    const current = structuredClone(this.currentSmartphotoImages$.getValue());

    const damageImageFile = current.find((image) => image.damage === damage.uuid);

    if (!damageImageFile) {
      this.currentSmartphotoImages$.next([
        ...current,
        { files: images.map((image) => ({ file: image, uuid: crypto.randomUUID() })), damage: damage.uuid }
      ]);

      return;
    }

    damageImageFile.files.push(...images.map((image) => ({ file: image, uuid: crypto.randomUUID() })));

    this.currentSmartphotoImages$.next(current);
  }

  private async uploadImages(smartphoto: Smartphoto): Promise<void> {
    const images = this.currentSmartphotoImages$.getValue();

    for await (const { files, damage } of images) {
      try {
        const foundDamage = smartphoto.getMainDamages().find((d) => d.uuid === damage);

        if (!foundDamage) {
          throw new Error('Damage not found');
        }

        const imageUuids = await this.damageImagesService.uploadDamageImages(
          smartphoto,
          foundDamage,
          files.map((damageImageFile) => damageImageFile.file)
        );

        DamageImagesService.addImagesToDamage(foundDamage, imageUuids);
      } catch (error) {
        console.error(error);
        BdeoToastService.error(this.i18NextService.t('brokers:error.upload_images'));
      }
    }
  }

  private async updateTransaction(smartphoto: Smartphoto): Promise<void> {
    await this.uploadImages(smartphoto);

    const mainReport = smartphoto.reports.findMainReport();
    const otherReports = smartphoto.reports.findOtherReport();
    const deletedReport = smartphoto.reports.findDeleteReport();
    const reports = [mainReport, ...otherReports, deletedReport].map((report) => report.export());
    const damageSummary = [...smartphoto.getAllDamages(), ...smartphoto.getDeletedDamages(), ...smartphoto.concepts].map((damage) =>
      damage.export()
    );

    cleanExportedDamages(damageSummary);

    try {
      smartphoto.waitingForAI = await this.updateDamages({
        selfadjustId: smartphoto.id,
        costEstimationInfo: smartphoto.estimationConfig,
        damageSummary,
        reports
      });
      this.smartphoto$.next(smartphoto);

      BdeoToastService.success(this.i18NextService.t('brokers:success_update_smartphoto'));
    } catch (e: unknown) {
      BdeoToastService.error(this.i18NextService.t('brokers:error.update_damages'));

      throw e;
    } finally {
      this.currentSmartphotoImages$.next([]);
    }
  }
}
